import { Link } from "react-router-dom";
import { ArrowRight, Heart, Sparkles, Star } from "lucide-react";
import { WEDDING_CITIES } from "@/weddingsConfig";

/**
 * Premium B2B section shown on CityPage: 3 cards (Weddings active / Events soon / Private soon).
 * Matches the dark+gold visual language of /weddings and /weddings/{city}.
 * Always links the active Weddings card to /weddings/{city} if a wedding landing exists for that city,
 * otherwise to the /weddings hub.
 */
export default function WeddingsBanner({ citySlug, lang = "ru" }) {
  const hasCityWedding = !!WEDDING_CITIES[citySlug];
  const cityName = hasCityWedding
    ? (lang === "en" ? WEDDING_CITIES[citySlug].nameEn : WEDDING_CITIES[citySlug].name)
    : "";
  const weddingsHref = hasCityWedding
    ? `/weddings/${citySlug}${lang !== "ru" ? `?lang=${lang}` : ""}`
    : `/weddings${lang !== "ru" ? `?lang=${lang}` : ""}`;

  const copy = {
    ru: {
      eyebrow: "B2B НАПРАВЛЕНИЯ",
      title: "Флористика для важных",
      titleAccent: "событий",
      subtitle: hasCityWedding
        ? `Премиум-оформление под ключ в ${cityName} и других городах. От букета невесты до банкета.`
        : "Премиум-оформление под ключ. Свадьбы, корпоративы, частные праздники.",
      services: [
        {
          slug: "weddings",
          name: "Свадьбы",
          desc: hasCityWedding
            ? `Полное оформление свадьбы в ${cityName}. От €2 500.`
            : "Букет невесты, арка, банкет, фотозона. От €2 500.",
          status: "active",
          icon: Heart,
        },
        { slug: "events", name: "Корпоративы и ивенты", desc: "Гала-ужины, конференции, открытия, годовщины брендов.", status: "soon", icon: Sparkles },
        { slug: "private", name: "Частные праздники", desc: "Дни рождения, юбилеи, помолвки, годовщины.", status: "soon", icon: Star },
      ],
      cta: "Открыть",
      soonLabel: "СКОРО",
    },
    en: {
      eyebrow: "B2B SERVICES",
      title: "Floristry for the",
      titleAccent: "moments that matter",
      subtitle: hasCityWedding
        ? `Turnkey premium design in ${cityName} and beyond. From bridal bouquet to banquet.`
        : "Turnkey premium design. Weddings, corporate events, private celebrations.",
      services: [
        {
          slug: "weddings",
          name: "Weddings",
          desc: hasCityWedding
            ? `Full wedding design in ${cityName}. From €2,500.`
            : "Bridal bouquet, arch, banquet, photo zone. From €2,500.",
          status: "active",
          icon: Heart,
        },
        { slug: "events", name: "Corporate & events", desc: "Gala dinners, conferences, brand openings, anniversaries.", status: "soon", icon: Sparkles },
        { slug: "private", name: "Private celebrations", desc: "Birthdays, jubilees, engagements, anniversaries.", status: "soon", icon: Star },
      ],
      cta: "Explore",
      soonLabel: "COMING SOON",
    },
    tr: {
      eyebrow: "B2B HIZMETLERI",
      title: "Onemli anlar icin",
      titleAccent: "ciceklendirme",
      subtitle: hasCityWedding
        ? `${cityName} ve diger sehirlerde anahtar teslim premium tasarim. Gelin buketinden banket dekoruna.`
        : "Anahtar teslim premium tasarim. Dugunler, kurumsal etkinlikler, ozel kutlamalar.",
      services: [
        {
          slug: "weddings",
          name: "Dugunler",
          desc: hasCityWedding
            ? `${cityName}'da tam dugun tasarimi. €2.500'den.`
            : "Gelin buketi, kemer, banket, foto alani. €2.500'den.",
          status: "active",
          icon: Heart,
        },
        { slug: "events", name: "Kurumsal ve etkinlikler", desc: "Gala yemekleri, konferanslar, marka acilislari, yildonumleri.", status: "soon", icon: Sparkles },
        { slug: "private", name: "Ozel kutlamalar", desc: "Dogum gunleri, jubileler, nisanlar, yildonumleri.", status: "soon", icon: Star },
      ],
      cta: "Kesfet",
      soonLabel: "YAKINDA",
    },
  };
  const t = copy[lang] || copy.ru;

  return (
    <section className="relative bg-gradient-to-br from-pink-50 via-white to-rose-50 py-14 sm:py-20 overflow-hidden" data-testid="weddings-banner">
      <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-pink-200/30 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-[420px] h-[420px] rounded-full bg-rose-200/30 blur-3xl pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-5 sm:px-6">
        <div className="text-center mb-10 sm:mb-14">
          <span className="inline-block bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs sm:text-sm font-semibold px-4 py-1.5 rounded-full mb-4 shadow-sm shadow-pink-200">
            {t.eyebrow}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}>
            {t.title}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-rose-500">{t.titleAccent}</span>
          </h2>
          <p className="text-sm sm:text-base text-gray-500 mt-4 sm:mt-5 max-w-xl mx-auto leading-relaxed">{t.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {t.services.map(s => {
            const Icon = s.icon;
            const isActive = s.status === "active";
            const cardCls = isActive
              ? "group relative bg-white rounded-3xl p-6 sm:p-7 border-2 border-pink-400 shadow-lg shadow-pink-100 hover:shadow-xl hover:border-pink-500 transition-all block"
              : "relative bg-white rounded-3xl p-6 sm:p-7 border-2 border-gray-100 hover:border-pink-200 transition-all";
            const content = (
              <>
                <div className="flex items-start justify-between mb-5">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 transition-transform ${
                    isActive
                      ? "bg-gradient-to-br from-pink-500 to-rose-500 shadow-lg shadow-pink-200 group-hover:scale-110"
                      : "bg-pink-50"
                  }`}>
                    <Icon className={`w-7 h-7 ${isActive ? "text-white" : "text-pink-300"}`} strokeWidth={1.8} />
                  </div>
                  {!isActive && (
                    <span className="text-[10px] tracking-[0.15em] font-semibold px-2.5 py-1 bg-pink-50 text-pink-500 rounded-full">
                      {t.soonLabel}
                    </span>
                  )}
                </div>
                <h3 className={`text-lg sm:text-xl font-bold mb-2 ${isActive ? "text-gray-900" : "text-gray-700"}`}
                    style={{ fontFamily: "'Playfair Display', serif" }}>
                  {s.name}
                </h3>
                <p className="text-xs sm:text-sm text-gray-500 leading-relaxed mb-5">{s.desc}</p>
                {isActive && (
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-pink-500 group-hover:text-pink-600 group-hover:gap-3 transition-all">
                    {t.cta} <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </>
            );
            return isActive ? (
              <Link key={s.slug} to={weddingsHref} className={cardCls} data-testid={`weddings-banner-${s.slug}`}>
                {content}
              </Link>
            ) : (
              <div key={s.slug} className={cardCls} data-testid={`weddings-banner-${s.slug}`}>
                {content}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
